/**
 * Generate demo cars: 35 (full replace) or 10 (add with full data in everything)
 */

import { vehiclesService, invoicesService } from '../services/firestoreService';
import { createInvoiceForVehicle } from './dataUtils';
import { auth } from '../firebase/config';

// Sample data for generating realistic vehicles
const MAKES_MODELS = [
  { make: 'Ford', models: ['Fiesta', 'Focus', 'Kuga', 'Puma', 'Mustang'] },
  { make: 'BMW', models: ['320d', '520d', 'X3', 'X5', '1 Series'] },
  { make: 'Audi', models: ['A3', 'A4', 'Q5', 'Q7', 'A6'] },
  { make: 'Mercedes-Benz', models: ['C-Class', 'E-Class', 'GLC', 'GLE', 'A-Class'] },
  { make: 'Volkswagen', models: ['Golf', 'Passat', 'Tiguan', 'Polo', 'T-Roc'] },
  { make: 'Toyota', models: ['Yaris', 'Corolla', 'RAV4', 'Prius', 'Camry'] },
  { make: 'Nissan', models: ['Qashqai', 'Juke', 'Leaf', 'Micra', 'X-Trail'] },
  { make: 'Vauxhall', models: ['Corsa', 'Astra', 'Insignia', 'Crossland', 'Grandland'] },
  { make: 'Hyundai', models: ['i20', 'i30', 'Tucson', 'Kona', 'Santa Fe'] },
  { make: 'Kia', models: ['Sportage', 'Niro', 'Ceed', 'Picanto', 'Sorento'] },
  { make: 'Land Rover', models: ['Discovery', 'Range Rover', 'Defender', 'Evoque', 'Velar'] },
  { make: 'Volvo', models: ['XC60', 'XC90', 'V40', 'V60', 'XC40'] },
  { make: 'Mini', models: ['Cooper', 'Countryman', 'Clubman', 'One', 'Convertible'] },
  { make: 'Peugeot', models: ['208', '308', '3008', '2008', '5008'] },
  { make: 'Skoda', models: ['Octavia', 'Fabia', 'Kodiaq', 'Kamiq', 'Superb'] }
];

const FIRST_NAMES = ['James', 'Sarah', 'Michael', 'Emma', 'David', 'Olivia', 'Robert', 'Sophia', 'William', 'Isabella', 'Richard', 'Charlotte', 'Joseph', 'Amelia', 'Thomas', 'Mia', 'Christopher', 'Harper', 'Daniel', 'Evelyn'];
const LAST_NAMES = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee'];
const TECHNICIANS = ['John Smith', 'Sarah Johnson', 'Mike Williams', 'Emma Brown', 'David Jones', 'Olivia Garcia', 'Robert Miller'];
const SITES = ['Main Site', 'Site A', 'Site B', 'Storage', 'Other'];

// Generate UK registration number
function generateReg() {
  const letters = 'ABCDEFGHJKLMNPRSTUVWXYZ';
  
  // Format: AB12 CDE (modern format)
  const part1 = letters[Math.floor(Math.random() * letters.length)] + letters[Math.floor(Math.random() * letters.length)];
  const part2 = String(Math.floor(Math.random() * 90) + 10);
  const part3 = letters[Math.floor(Math.random() * letters.length)] + letters[Math.floor(Math.random() * letters.length)] + letters[Math.floor(Math.random() * letters.length)];
  
  return `${part1}${part2} ${part3}`;
}

// Generate vehicle name
function generateVehicleName() {
  const makeModel = MAKES_MODELS[Math.floor(Math.random() * MAKES_MODELS.length)];
  const model = makeModel.models[Math.floor(Math.random() * makeModel.models.length)];
  const variants = ['Sport', 'SE', 'Titanium', 'M Sport', 'S-Line', 'AMG Line', 'R-Line', 'GT', 'Premium', 'Luxury'];
  const variant = Math.random() > 0.5 ? ` ${variants[Math.floor(Math.random() * variants.length)]}` : '';
  const year = 2018 + Math.floor(Math.random() * 6);
  return `${makeModel.make} ${model}${variant} ${year}`;
}

// Generate customer name
function generateCustomerName() {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  return `${firstName} ${lastName}`;
}

// Generate phone number
function generatePhone() {
  const prefixes = ['07', '01'];
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
  const number = Math.floor(Math.random() * 900000000) + 100000000;
  return `${prefix}${number.toString().substring(0, 2)} ${number.toString().substring(2, 6)} ${number.toString().substring(6)}`;
}

// Generate date within last 60 days
function generateDate(daysAgo = 0) {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo - Math.floor(Math.random() * 30));
  return date.toISOString().split('T')[0];
}

// Generate MOT status
function generateMOTStatus() {
  const statuses = [
    'MOT valid until ' + generateFutureDate(30, 365),
    'MOT due ' + generateFutureDate(0, 30),
    'MOT expired ' + generatePastDate(1, 30)
  ];
  return statuses[Math.floor(Math.random() * statuses.length)];
}

function generateFutureDate(minDays, maxDays) {
  const date = new Date();
  date.setDate(date.getDate() + minDays + Math.floor(Math.random() * (maxDays - minDays)));
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function generatePastDate(minDays, maxDays) {
  const date = new Date();
  date.setDate(date.getDate() - minDays - Math.floor(Math.random() * (maxDays - minDays)));
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function generateNextServiceDue() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() + Math.floor(Math.random() * 60) - 30); // +/- 30 days from 1 year
  return d.toISOString().split('T')[0];
}

// Generate key code
function generateKeyCode() {
  const lockers = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
  const locker = lockers[Math.floor(Math.random() * lockers.length)] + String(Math.floor(Math.random() * 9) + 1);
  const code = Math.floor(Math.random() * 9000) + 1000;
  return `Locker ${locker} · Code ${code}`;
}

// Generate summary based on status
function generateSummary(status) {
  const summaries = {
    'MOT': [
      'MOT test · Full inspection · Emissions check',
      'MOT test · Brake test · Safety inspection',
      'MOT test · Lights check · Tyre inspection'
    ],
    'Bodyshop': [
      'Panel repair · Paint work · Dent removal',
      'Full respray · Bodywork repair · Rust treatment',
      'Bumper replacement · Paint match · Polish'
    ],
    'Workshop': [
      'Service · Oil change · Filter replacement',
      'Brake service · Disc and pad replacement',
      'Engine diagnostic · Repair · Testing',
      'Transmission service · Fluid change',
      'Suspension repair · Alignment check',
      'Electrical fault · Wiring repair · Testing'
    ],
    'Tyres': [
      '4x tyres · Supply and fit · 205/55 R16',
      'Front axle · 2 tyres · Fit only',
      'Full set · Michelin CrossClimate · Supply and fit',
      '2x rear tyres · 225/45 R17 · Fit only',
      'Tyre replacement · Balance and fit'
    ],
    'Awaiting Parts': [
      'Awaiting brake pads · Service on hold',
      'Awaiting engine parts · Diagnostic complete',
      'Awaiting body panels · Repair pending',
      'Awaiting electrical components · Fault identified',
      'Awaiting suspension parts · Inspection complete'
    ],
    'Sales': [
      'Sales prep · Full inspection · Documentation',
      'Vehicle preparation · Valet · Quality check',
      'Sales ready · Full service history · HPI clear',
      'Premium vehicle · Full preparation completed',
      'Sales prep · MOT · Service · Polish'
    ]
  };
  
  const statusSummaries = summaries[status] || summaries['Workshop'];
  return statusSummaries[Math.floor(Math.random() * statusSummaries.length)];
}

/**
 * Generate 35 demo vehicles distributed across various statuses
 */
export async function generateDemoCars() {
  try {
    const isAuthenticated = auth.currentUser !== null;
    
    if (!isAuthenticated) {
      throw new Error('You must be logged in to generate demo cars');
    }

    console.log('🗑️  Removing all existing demo data...');
    
    // Remove all vehicles
    let deletedVehicles = 0;
    if (isAuthenticated) {
      deletedVehicles = await vehiclesService.deleteAll();
      console.log(`✅ Deleted ${deletedVehicles} existing vehicles from Firestore`);
    }
    
    // Remove all invoices
    let deletedInvoices = 0;
    if (isAuthenticated) {
      deletedInvoices = await invoicesService.deleteAll();
      console.log(`✅ Deleted ${deletedInvoices} existing invoices from Firestore`);
    }
    
    console.log('\n🚗 Generating 35 demo vehicles...');
    
    const vehicles = [];
    
    // Generate vehicles by status
    const statusDistribution = {
      'MOT': 7,
      'Bodyshop': 6,
      'Workshop': 6,
      'Tyres': 4,
      'In Progress': 4,
      'Sales': 10
    };
    
    // MOT vehicles (7)
    for (let i = 0; i < statusDistribution['MOT']; i++) {
      const dropOffDays = Math.floor(Math.random() * 30);
      vehicles.push({
        reg: generateReg(),
        vehicle: generateVehicleName(),
        status: 'MOT',
        summary: generateSummary('MOT'),
        technician: TECHNICIANS[Math.floor(Math.random() * TECHNICIANS.length)],
        customerName: generateCustomerName(),
        contact: generatePhone(),
        keyCode: generateKeyCode(),
        cost: Math.floor(Math.random() * 200) + 50, // £50-£250
        partsOnOrder: false,
        notes: '',
        motStatus: generateMOTStatus(),
        taxStatus: 'Tax valid',
        nextServiceDue: generateNextServiceDue(),
        paid: Math.random() > 0.7,
        collected: false,
        dropOffDate: generateDate(dropOffDays),
        receivedDate: generateDate(dropOffDays),
        collectionDate: null,
        location: SITES[Math.floor(Math.random() * SITES.length)],
        images: []
      });
    }
    
    // Bodyshop vehicles (6)
    for (let i = 0; i < statusDistribution['Bodyshop']; i++) {
      const dropOffDays = Math.floor(Math.random() * 45);
      vehicles.push({
        reg: generateReg(),
        vehicle: generateVehicleName(),
        status: 'Bodyshop',
        summary: generateSummary('Bodyshop'),
        technician: TECHNICIANS[Math.floor(Math.random() * TECHNICIANS.length)],
        customerName: generateCustomerName(),
        contact: generatePhone(),
        keyCode: generateKeyCode(),
        cost: Math.floor(Math.random() * 2000) + 500, // £500-£2500
        partsOnOrder: Math.random() > 0.6,
        notes: '',
        motStatus: generateMOTStatus(),
        taxStatus: 'Tax valid',
        nextServiceDue: generateNextServiceDue(),
        paid: Math.random() > 0.8,
        collected: false,
        dropOffDate: generateDate(dropOffDays),
        receivedDate: generateDate(dropOffDays),
        collectionDate: null,
        location: SITES[Math.floor(Math.random() * SITES.length)],
        images: []
      });
    }
    
    // Workshop vehicles
    for (let i = 0; i < statusDistribution['Workshop']; i++) {
      const dropOffDays = Math.floor(Math.random() * 30);
      vehicles.push({
        reg: generateReg(),
        vehicle: generateVehicleName(),
        status: 'Workshop',
        summary: generateSummary('Workshop'),
        technician: TECHNICIANS[Math.floor(Math.random() * TECHNICIANS.length)],
        customerName: generateCustomerName(),
        contact: generatePhone(),
        keyCode: generateKeyCode(),
        cost: Math.floor(Math.random() * 800) + 100, // £100-£900
        partsOnOrder: Math.random() > 0.7,
        notes: '',
        motStatus: generateMOTStatus(),
        taxStatus: 'Tax valid',
        nextServiceDue: generateNextServiceDue(),
        paid: Math.random() > 0.75,
        collected: false,
        dropOffDate: generateDate(dropOffDays),
        receivedDate: generateDate(dropOffDays),
        collectionDate: null,
        location: SITES[Math.floor(Math.random() * SITES.length)],
        images: []
      });
    }

    // Tyres vehicles
    const tyreSizes = ['205/55 R16', '225/45 R17', '195/65 R15', '215/55 R17', ''];
    const tyreServiceTypes = ['Supply only', 'Fit only', 'Supply and fit', ''];
    for (let i = 0; i < statusDistribution['Tyres']; i++) {
      const dropOffDays = Math.floor(Math.random() * 14);
      vehicles.push({
        reg: generateReg(),
        vehicle: generateVehicleName(),
        status: 'Tyres',
        summary: generateSummary('Tyres'),
        technician: TECHNICIANS[Math.floor(Math.random() * TECHNICIANS.length)],
        customerName: generateCustomerName(),
        contact: generatePhone(),
        keyCode: generateKeyCode(),
        cost: Math.floor(Math.random() * 400) + 80, // £80-£480
        partsOnOrder: false,
        notes: '',
        motStatus: generateMOTStatus(),
        taxStatus: 'Tax valid',
        paid: Math.random() > 0.7,
        collected: false,
        dropOffDate: generateDate(dropOffDays),
        receivedDate: generateDate(dropOffDays),
        collectionDate: null,
        location: SITES[Math.floor(Math.random() * SITES.length)],
        images: [],
        tyreSize: tyreSizes[Math.floor(Math.random() * tyreSizes.length)],
        tyreQuantity: ['2', '4', 'Front axle', 'Full set'][Math.floor(Math.random() * 4)],
        tyrePosition: ['', 'Full set', 'FL, FR, RL, RR'][Math.floor(Math.random() * 4)],
        tyreServiceType: tyreServiceTypes[Math.floor(Math.random() * tyreServiceTypes.length)],
        treadFL: '4.2', treadFR: '4.1', treadRL: '3.8', treadRR: '3.9', treadSP: '5.0',
        tyreSizeFront: '205/55 R16', tyreSizeRear: '205/55 R16', tyreRate: '91V',
        tyreMakeFront: 'Michelin', tyreMakeRear: 'Michelin', loadIndex: '91',
        pressureFL: '32', pressureFR: '32', pressureRL: '32', pressureRR: '32', pressureSP: '32', wheelTorqueNM: '110'
      });
    }
    
    // In Progress vehicles (4)
    for (let i = 0; i < statusDistribution['In Progress']; i++) {
      const dropOffDays = Math.floor(Math.random() * 60);
      vehicles.push({
        reg: generateReg(),
        vehicle: generateVehicleName(),
        status: 'In Progress',
        previousStatus: 'Workshop',
        summary: generateSummary('Workshop'),
        technician: TECHNICIANS[Math.floor(Math.random() * TECHNICIANS.length)],
        customerName: generateCustomerName(),
        contact: generatePhone(),
        keyCode: generateKeyCode(),
        cost: Math.floor(Math.random() * 1500) + 200, // £200-£1700
        partsOnOrder: true,
        notes: 'Work in progress.',
        motStatus: generateMOTStatus(),
        taxStatus: 'Tax valid',
        nextServiceDue: generateNextServiceDue(),
        paid: false,
        collected: false,
        dropOffDate: generateDate(dropOffDays),
        receivedDate: generateDate(dropOffDays),
        collectionDate: null,
        location: SITES[Math.floor(Math.random() * SITES.length)],
        images: []
      });
    }
    
    // Sales vehicles (10) with various deal stages
    const salesStages = ['inStock', 'inPrep', 'forSale', 'depositTaken', 'sold'];
    for (let i = 0; i < statusDistribution['Sales']; i++) {
      const dropOffDays = Math.floor(Math.random() * 90);
      const stageIndex = Math.floor(Math.random() * salesStages.length);
      const dealStage = salesStages[stageIndex];
      const basePrice = Math.floor(Math.random() * 20000) + 5000; // £5k-£25k
      
      vehicles.push({
        reg: generateReg(),
        vehicle: generateVehicleName(),
        status: 'Sales',
        summary: generateSummary('Sales'),
        technician: '', // Sales vehicles don't have technicians
        customerName: dealStage === 'inStock' ? '' : generateCustomerName(),
        contact: dealStage === 'inStock' ? '' : generatePhone(),
        keyCode: generateKeyCode(),
        cost: Math.floor(basePrice * 0.3), // Prep cost ~30% of sale price
        prepCost: Math.floor(basePrice * 0.1),
        warranty: Math.floor(basePrice * 0.05),
        misc: Math.floor(Math.random() * 500),
        salePrice: basePrice,
        dealStage: dealStage,
        partsOnOrder: false,
        notes: '',
        motStatus: generateMOTStatus(),
        taxStatus: 'Tax valid',
        nextServiceDue: generateNextServiceDue(),
        paid: dealStage === 'sold' || dealStage === 'collected',
        collected: dealStage === 'collected',
        dropOffDate: generateDate(dropOffDays),
        receivedDate: generateDate(dropOffDays),
        collectionDate: dealStage === 'collected' ? generateDate(Math.floor(Math.random() * 10)) : null,
        location: SITES[Math.floor(Math.random() * SITES.length)],
        images: []
      });
    }
    
    // Shuffle vehicles to mix up the order
    for (let i = vehicles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [vehicles[i], vehicles[j]] = [vehicles[j], vehicles[i]];
    }
    
    console.log(`\n📦 Adding ${vehicles.length} vehicles...`);
    
    // Add vehicles to Firestore only
    if (isAuthenticated) {
      // Use batch add for Firestore
      const added = await vehiclesService.batchAdd(vehicles);
      console.log(`✅ Added ${added} vehicles to Firestore`);
    } else {
      throw new Error('You must be logged in to generate demo cars');
    }
    
    // Show distribution
    console.log('\n📊 Vehicle Distribution:');
    const distribution = {};
    vehicles.forEach(v => {
      distribution[v.status] = (distribution[v.status] || 0) + 1;
    });
    Object.entries(distribution).forEach(([status, count]) => {
      console.log(`   ${status}: ${count} vehicles`);
    });
    
    if (statusDistribution['Sales']) {
      const salesVehicles = vehicles.filter(v => v.status === 'Sales');
      const salesDistribution = {};
      salesVehicles.forEach(v => {
        salesDistribution[v.dealStage] = (salesDistribution[v.dealStage] || 0) + 1;
      });
      console.log('\n📈 Sales Deal Stages:');
      Object.entries(salesDistribution).forEach(([stage, count]) => {
        console.log(`   ${stage}: ${count} vehicles`);
      });
    }
    
    console.log('\n✅ Demo cars generation complete!');
    console.log('🔄 Reloading page in 2 seconds...');
    
    // Reload the page to see changes
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
    
    return {
      success: true,
      vehiclesCreated: vehicles.length,
      deletedVehicles,
      deletedInvoices,
      distribution
    };
  } catch (error) {
    console.error('❌ Error generating demo cars:', error);
    throw error;
  }
}

/**
 * Generate exactly 10 demo vehicles with full data in every field, plus 10 invoices.
 * Does NOT delete existing data – adds 10 vehicles and 10 invoices to Firebase.
 */
export async function generate10DemoVehicles() {
  try {
    if (auth.currentUser === null) {
      throw new Error('You must be logged in to add demo data');
    }

    console.log('🚗 Adding 10 demo vehicles with full data + 10 invoices...');

    const vehicles = [];
    const statuses = [
      { status: 'MOT', count: 2 },
      { status: 'Bodyshop', count: 2 },
      { status: 'Workshop', count: 2 },
      { status: 'Tyres', count: 1 },
      { status: 'Sales', count: 3 }
    ];

    const usedRegs = new Set();

    function uniqueReg() {
      let r;
      do {
        r = generateReg();
      } while (usedRegs.has(r));
      usedRegs.add(r);
      return r;
    }

    for (const { status, count } of statuses) {
      for (let i = 0; i < count; i++) {
        const dropOffDays = Math.floor(Math.random() * 45);
        const base = {
          reg: uniqueReg(),
          vehicle: generateVehicleName(),
          status,
          summary: generateSummary(status),
          technician: status === 'Sales' ? '' : TECHNICIANS[Math.floor(Math.random() * TECHNICIANS.length)],
          customerName: status === 'Sales' && Math.random() < 0.4 ? '' : generateCustomerName(),
          contact: status === 'Sales' && Math.random() < 0.4 ? '' : generatePhone(),
          keyCode: generateKeyCode(),
          notes: 'Demo vehicle. Customer notified. Keys in key cabinet.',
          motStatus: generateMOTStatus(),
          taxStatus: 'Tax valid',
          nextServiceDue: generateNextServiceDue(),
          paid: status === 'Sales' ? Math.random() > 0.5 : Math.random() > 0.6,
          collected: false,
          dropOffDate: generateDate(dropOffDays),
          receivedDate: generateDate(dropOffDays),
          collectionDate: null,
          location: SITES[Math.floor(Math.random() * SITES.length)],
          images: [],
          partsOnOrder: Math.random() > 0.7
        };

        if (status === 'MOT') {
          vehicles.push({
            ...base,
            cost: Math.floor(Math.random() * 200) + 50,
            includeVat: false
          });
        } else if (status === 'Bodyshop') {
          const labour = Math.floor(Math.random() * 800) + 400;
          const parts = Math.floor(Math.random() * 400) + 100;
          vehicles.push({
            ...base,
            cost: labour + parts,
            includeVat: true,
            lineItems: [
              { description: 'Labour – panel repair and paint', cost: labour },
              { description: 'Paint and materials', cost: parts }
            ]
          });
        } else if (status === 'Workshop') {
          const labour = Math.floor(Math.random() * 200) + 80;
          const parts = Math.floor(Math.random() * 150) + 30;
          vehicles.push({
            ...base,
            cost: labour + parts,
            includeVat: true,
            lineItems: [
              { description: 'Labour – service and repair', cost: labour },
              { description: 'Parts and consumables', cost: parts }
            ]
          });
        } else if (status === 'Tyres') {
          vehicles.push({
            ...base,
            cost: Math.floor(Math.random() * 600) + 200,
            includeVat: true
          });
        } else {
          const salePrice = Math.floor(Math.random() * 18000) + 7000;
          const dealStage = ['inStock', 'inPrep', 'forSale', 'depositTaken', 'sold'][Math.floor(Math.random() * 5)];
          vehicles.push({
            ...base,
            cost: Math.floor(salePrice * 0.25),
            prepCost: Math.floor(salePrice * 0.08),
            warranty: Math.floor(salePrice * 0.04),
            misc: Math.floor(Math.random() * 300),
            salePrice,
            dealStage,
            collected: dealStage === 'sold' ? Math.random() > 0.5 : false,
            collectionDate: dealStage === 'sold' && base.collected ? generateDate(5) : null
          });
        }
      }
    }

    await vehiclesService.batchAdd(vehicles);
    console.log(`✅ Added ${vehicles.length} vehicles to Firestore`);

    let invoicesCreated = 0;
    for (const veh of vehicles) {
      try {
        const inv = await createInvoiceForVehicle(veh);
        if (inv) invoicesCreated++;
      } catch (e) {
        console.warn('Invoice skip for', veh.reg, e.message);
      }
    }
    console.log(`✅ Created ${invoicesCreated} invoices in Firestore`);

    console.log('✅ 10 demo vehicles + invoices added. Reload to see them.');
    if (typeof window !== 'undefined') {
      setTimeout(() => window.location.reload(), 1500);
    }

    return {
      success: true,
      vehiclesCreated: vehicles.length,
      invoicesCreated
    };
  } catch (error) {
    console.error('❌ Error adding 10 demo vehicles:', error);
    throw error;
  }
}

/**
 * Add 20 richer demo vehicles that cover every visible workflow bucket.
 * Does NOT delete existing data.
 */
export async function generate20DemoVehicles() {
  try {
    if (auth.currentUser === null) {
      throw new Error('You must be logged in to add demo data');
    }

    console.log('🚗 Adding 20 rich demo vehicles...');

    const usedRegs = new Set();
    const usedSlots = new Set();
    const vehicles = [];
    const today = new Date();

    const scenarioTemplates = [
      { status: 'MOT', label: 'MOT while-you-wait', paid: false },
      { status: 'MOT', label: 'MOT + advisories', paid: true },
      { status: 'MOT', label: 'MOT re-test', paid: false },
      { status: 'Bodyshop', label: 'Rear bumper repair', paid: false },
      { status: 'Bodyshop', label: 'Quarter panel paint', paid: true },
      { status: 'Bodyshop', label: 'Insurance repair', paid: false },
      { status: 'Workshop', label: 'Major service', paid: false },
      { status: 'Workshop', label: 'Brake overhaul', paid: true },
      { status: 'Workshop', label: 'Diagnostic fault trace', paid: false },
      { status: 'Workshop', label: 'Clutch replacement', paid: false },
      { status: 'Tyres', label: 'Front tyres supply and fit', paid: true },
      { status: 'Tyres', label: '4-wheel tyre package', paid: false },
      { status: 'Tyres', label: 'Puncture and valve service', paid: false },
      { status: 'In Progress', previousStatus: 'Workshop', label: 'Engine misfire investigation', paid: false },
      { status: 'In Progress', previousStatus: 'Bodyshop', label: 'Paint curing and polish', paid: false },
      { status: 'In Progress', previousStatus: 'MOT', label: 'MOT fail — brake work', paid: false },
      { status: 'In Progress', previousStatus: 'Workshop', label: 'Service in progress', paid: false },
      { status: 'Sales', dealStage: 'inStock', label: 'Fresh stock arrival', paid: false },
      { status: 'Sales', dealStage: 'forSale', label: 'Forecourt ready', paid: false },
      { status: 'Sales', dealStage: 'depositTaken', label: 'Deposit taken collection booked', paid: true }
    ];

    function uniqueReg() {
      let r;
      do {
        r = generateReg();
      } while (usedRegs.has(r));
      usedRegs.add(r);
      return r;
    }

    function isoDate(daysOffset) {
      const d = new Date(today);
      d.setDate(d.getDate() + daysOffset);
      return d.toISOString().split('T')[0];
    }

    function uniqueMotTimeSlot(dropOffDate, seed) {
      const options = ['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30'];
      for (let i = 0; i < options.length; i++) {
        const time = options[(seed + i) % options.length];
        const key = `${dropOffDate}T${time}`;
        if (!usedSlots.has(key)) {
          usedSlots.add(key);
          return key;
        }
      }
      return `${dropOffDate}T16:00`;
    }

    function salesDocs(index) {
      return {
        v5c: index % 2 === 0 ? 'Present' : 'Applied for',
        keys: index % 3 === 0 ? '2 keys' : '1 key',
        import: index % 4 === 0 ? 'No' : '',
        serviceHistory: index % 2 === 0 ? 'Full history' : 'Part history'
      };
    }

    scenarioTemplates.forEach((scenario, index) => {
      const dropOffDate = isoDate(-Math.min(index + 1, 12));
      const receivedDate = dropOffDate;
      const customerName = scenario.status === 'Sales' && scenario.dealStage === 'inStock'
        ? ''
        : generateCustomerName();
      const contact = scenario.status === 'Sales' && scenario.dealStage === 'inStock'
        ? ''
        : generatePhone();
      const base = {
        reg: uniqueReg(),
        vehicle: generateVehicleName(),
        status: scenario.status,
        previousStatus: scenario.previousStatus || '',
        summary: scenario.label,
        technician: scenario.status === 'Sales' ? '' : TECHNICIANS[index % TECHNICIANS.length],
        customerName,
        contact,
        email: customerName ? `demo${index + 1}@example.com` : '',
        address: customerName ? `${index + 11} Demo Street` : '',
        postcode: customerName ? `AB${10 + (index % 80)} ${index % 9}CD` : '',
        city: customerName ? 'London' : '',
        keyCode: generateKeyCode(),
        notes: `Demo record: ${scenario.label}. Added to show workflow coverage.`,
        motStatus: scenario.status === 'Sales' ? generateMOTStatus() : generateMOTStatus(),
        taxStatus: index % 5 === 0 ? 'Tax due soon' : 'Tax valid',
        nextServiceDue: generateNextServiceDue(),
        mileage: String(22000 + index * 3711),
        paid: scenario.paid,
        collected: false,
        dropOffDate,
        receivedDate,
        collectionDate: null,
        location: SITES[index % SITES.length],
        images: [],
        partsOnOrder: false,
      };

      if (scenario.status === 'MOT') {
        const cost = 54 + (index * 7);
        vehicles.push({
          ...base,
          timeSlot: uniqueMotTimeSlot(dropOffDate, index),
          cost,
          estimatedCost: cost,
          includeVat: false
        });
        return;
      }

      if (scenario.status === 'Bodyshop') {
        const labourCost = 520 + (index * 35);
        const lineItems = [
          { description: 'Paint and materials', cost: 185 + (index * 10) },
          { description: 'Trim clips and consumables', cost: 42 + index }
        ];
        vehicles.push({
          ...base,
          cost: labourCost + lineItems.reduce((sum, item) => sum + item.cost, 0),
          estimatedCost: labourCost + lineItems.reduce((sum, item) => sum + item.cost, 0),
          labourCost,
          includeVat: true,
          lineItems
        });
        return;
      }

      if (scenario.status === 'Workshop' || scenario.status === 'In Progress') {
        const labourCost = 140 + (index * 18);
        const lineItems = [
          { description: 'Labour', cost: labourCost },
          { description: 'Parts and consumables', cost: 55 + (index * 6) }
        ];
        vehicles.push({
          ...base,
          cost: lineItems.reduce((sum, item) => sum + item.cost, 0),
          estimatedCost: lineItems.reduce((sum, item) => sum + item.cost, 0),
          labourCost,
          includeVat: true,
          lineItems,
          partsOnOrder: false
        });
        return;
      }

      if (scenario.status === 'Tyres') {
        const qty = index % 2 === 0 ? '2' : '4';
        const tyreCost = qty === '4' ? 420 : 210;
        vehicles.push({
          ...base,
          cost: tyreCost,
          estimatedCost: tyreCost,
          includeVat: true,
          tyreSize: index % 2 === 0 ? '225/45 R17' : '205/55 R16',
          tyreQuantity: qty,
          tyrePosition: qty === '4' ? 'Full set' : 'FL, FR',
          tyreServiceType: index % 3 === 0 ? 'Supply and fit' : 'Fit only',
          treadFL: '3.2',
          treadFR: '2.8',
          treadRL: '4.9',
          treadRR: '5.0',
          treadSP: '5.5',
          tyreSizeFront: index % 2 === 0 ? '225/45 R17' : '205/55 R16',
          tyreSizeRear: index % 2 === 0 ? '225/45 R17' : '205/55 R16',
          tyreRate: '94W',
          tyreMakeFront: 'Michelin',
          tyreMakeRear: 'Michelin',
          loadIndex: '94',
          pressureFL: '34',
          pressureFR: '34',
          pressureRL: '32',
          pressureRR: '32',
          pressureSP: '60',
          wheelTorqueNM: '110'
        });
        return;
      }

      const salePrice = 7995 + (index * 725);
      const dealStage = scenario.dealStage || 'inStock';
      vehicles.push({
        ...base,
        ...salesDocs(index),
        cost: Math.round(salePrice * 0.72),
        prepCost: Math.round(salePrice * 0.08),
        warranty: Math.round(salePrice * 0.03),
        misc: 145 + (index * 10),
        salePrice,
        dealStage,
        paid: dealStage === 'depositTaken' ? true : scenario.paid,
        collected: false,
        collectionDate: dealStage === 'depositTaken' ? isoDate(3) : null
      });
    });

    await vehiclesService.batchAdd(vehicles);
    console.log(`✅ Added ${vehicles.length} vehicles to Firestore`);

    let invoicesCreated = 0;
    for (const veh of vehicles) {
      try {
        const inv = await createInvoiceForVehicle(veh);
        if (inv) invoicesCreated++;
      } catch (e) {
        console.warn('Invoice skip for', veh.reg, e.message);
      }
    }

    console.log(`✅ Created ${invoicesCreated} invoices in Firestore`);
    console.log('🔄 Reloading page in 1.5 seconds...');
    if (typeof window !== 'undefined') {
      setTimeout(() => window.location.reload(), 1500);
    }

    return {
      success: true,
      vehiclesCreated: vehicles.length,
      invoicesCreated
    };
  } catch (error) {
    console.error('❌ Error adding 20 demo vehicles:', error);
    throw error;
  }
}

// Make it available globally for easy console access
if (typeof window !== 'undefined') {
  window.generateDemoCars = generateDemoCars;
  window.generate10DemoVehicles = generate10DemoVehicles;
  window.generate20DemoVehicles = generate20DemoVehicles;
}

